<script lang="ts">
  type Props = {
    value?: string[];
    options: string[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    maxHeight?: string;
    showSelectAll?: boolean;
    onchange?: () => void;
  };

  let {
    value = $bindable([]),
    options = [],
    label = '',
    placeholder = 'Select...',
    disabled = false,
    maxHeight = '300px',
    showSelectAll = false,
    onchange = undefined
  }: Props = $props();

  let dropdownOpen = $state(false);
  let searchValue = $state('');
  let buttonRef: HTMLButtonElement | undefined = $state();
  let dropdownStyle = $state('');

  // Filtered options based on search
  const filteredOptions = $derived(
    options.filter(opt =>
      opt.toLowerCase().includes(searchValue.toLowerCase())
    )
  );

  // Check if all options are selected
  const allSelected = $derived(
    options.length > 0 && value.length === options.length
  );

  function toggleOption(option: string) {
    if (value.includes(option)) {
      value = value.filter(v => v !== option);
    } else {
      value = [...value, option];
    }
    onchange?.();
  }

  function toggleSelectAll() {
    if (allSelected) {
      // Unselect all
      value = [];
    } else {
      // Select all
      value = [...options];
    }
    onchange?.();
  }

  // Calculate dropdown position and handle events
  $effect(() => {
    if (dropdownOpen && buttonRef) {
      const rect = buttonRef.getBoundingClientRect();
      dropdownStyle = `position: fixed; top: ${rect.bottom + 4}px; left: ${rect.left}px; width: ${rect.width}px;`;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          dropdownOpen = false;
          searchValue = '';
        }
      };

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const dropdownWrapper = target.closest('.multi-select');
        if (!dropdownWrapper) {
          dropdownOpen = false;
          searchValue = '';
        }
      };

      window.addEventListener('keydown', handleEscape, true); // Use capture phase
      window.addEventListener('click', handleClickOutside);
      return () => {
        window.removeEventListener('keydown', handleEscape, true);
        window.removeEventListener('click', handleClickOutside);
      };
    } else {
      // Clear search when dropdown closes
      searchValue = '';
      dropdownStyle = '';
    }
  });
</script>

<div class="multi-select">
  {#if label}
    <label>{label} ({value.length} selected)</label>
  {/if}
  <div class="dropdown-wrapper">
    <button
      bind:this={buttonRef}
      class="dropdown-button"
      onclick={() => !disabled && (dropdownOpen = !dropdownOpen)}
      type="button"
      {disabled}
    >
      {value.length === 0 ? placeholder : `${value.length} selected`}
      <span class="arrow">{dropdownOpen ? '▲' : '▼'}</span>
    </button>
    {#if dropdownOpen}
      <div class="dropdown-panel" style="{dropdownStyle} max-height: {maxHeight};">
        <div class="dropdown-header">
          <input
            type="text"
            class="search-input"
            placeholder="Search..."
            bind:value={searchValue}
            onclick={(e) => e.stopPropagation()}
          />
          {#if showSelectAll}
            <button
              class="select-all-btn"
              class:all-selected={allSelected}
              onclick={toggleSelectAll}
              title={allSelected ? "Unselect All" : "Select All"}
              type="button"
            >
              {#if allSelected}
                <!-- Unselect All icon (square with X) -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                </svg>
              {:else}
                <!-- Select All icon (checkmark in square) -->
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                </svg>
              {/if}
            </button>
          {/if}
        </div>
        <div class="checkbox-list">
          {#each filteredOptions as option}
            <label class="checkbox-item">
              <input
                type="checkbox"
                checked={value.includes(option)}
                onchange={() => toggleOption(option)}
              />
              <span>{option}</span>
            </label>
          {/each}
          {#if filteredOptions.length === 0}
            <div class="no-results">No matches found</div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .multi-select {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    position: relative;
  }

  label {
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
  }

  .dropdown-wrapper {
    position: relative;
  }

  .dropdown-button {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-md);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    background: var(--color-background);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all var(--transition-fast);
    text-align: left;
    color: var(--color-text-primary);
  }

  .dropdown-button:hover:not(:disabled) {
    border-color: var(--color-text-muted);
  }

  .dropdown-button:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-focus-ring);
  }

  .dropdown-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .arrow {
    font-size: 0.7em;
    color: var(--color-text-muted);
  }

  .dropdown-panel {
    z-index: var(--z-modal);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    min-width: 250px;
  }

  .dropdown-header {
    display: flex;
    gap: var(--spacing-xs);
    padding: var(--spacing-xs);
    border-bottom: 1px solid var(--color-border);
    background: var(--color-background-secondary);
    align-items: center;
    flex-shrink: 0;
  }

  .search-input {
    flex: 1;
    min-width: 0;
    padding: var(--spacing-sm);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    background: var(--color-background);
  }

  .search-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-focus-ring-sm);
  }

  .select-all-btn {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-sm);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-background);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  .select-all-btn:hover {
    background: var(--color-background-secondary);
    color: var(--color-primary);
    border-color: var(--color-primary);
  }

  .select-all-btn:active {
    transform: scale(0.95);
  }

  .select-all-btn.all-selected {
    background: var(--color-primary-light);
    color: var(--color-primary);
    border-color: var(--color-primary);
  }

  .select-all-btn.all-selected:hover {
    background: var(--color-primary);
    color: white;
  }

  .select-all-btn svg {
    display: block;
  }

  .checkbox-list {
    padding: var(--spacing-xs);
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .checkbox-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    padding: var(--spacing-xs) var(--spacing-sm);
    cursor: pointer;
    border-radius: var(--radius-sm);
    transition: background var(--transition-fast);
  }

  .checkbox-item:hover {
    background: var(--color-background-secondary);
  }

  .checkbox-item input[type="checkbox"] {
    cursor: pointer;
  }

  .checkbox-item span {
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
  }

  .no-results {
    padding: var(--spacing-md);
    text-align: center;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
  }
</style>
